import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCHF = (n) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

const fmtDay = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="text-gray-500">{fmtDay(label)}</p>
      <p className="font-semibold text-teal-700">{fmtCHF(payload[0].value)}</p>
    </div>
  );
};

const RevenueTimelineChart = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studioFilter, setStudioFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/studios/revenue-timeline?days=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const chartData = useMemo(() => {
    if (!data?.timeline) return [];
    if (studioFilter === 'all') return data.timeline;
    return data.timeline.map((d) => ({
      date: d.date,
      revenue: d.by_studio?.[studioFilter] ?? 0,
    }));
  }, [data, studioFilter]);

  const total =
    studioFilter === 'all'
      ? data?.total
      : data?.totals_by_studio?.[studioFilter] ?? 0;

  if (loading) {
    return (
      <Card data-testid="revenue-timeline-loading">
        <CardContent className="py-8 flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Umsatz-Verlauf wird geladen…
        </CardContent>
      </Card>
    );
  }

  if (!data?.timeline?.length) return null;

  const studios = data.studios || [];
  const showFilter = studios.length > 1;

  return (
    <Card data-testid="revenue-timeline-chart">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="w-5 h-5 text-teal-600" />
            Umsatz-Verlauf (letzte 30 Tage)
          </CardTitle>
          <div className="flex items-center gap-3">
            {showFilter && (
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs" data-testid="revenue-timeline-studio-filter">
                <button
                  data-testid="revenue-filter-all"
                  onClick={() => setStudioFilter('all')}
                  className={`px-3 py-1.5 font-medium transition-colors ${
                    studioFilter === 'all'
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Alle
                </button>
                {studios.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`revenue-filter-studio-${s.id}`}
                    onClick={() => setStudioFilter(s.id)}
                    className={`px-3 py-1.5 font-medium border-l border-gray-200 transition-colors ${
                      studioFilter === s.id
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
            <span className="text-sm font-semibold text-teal-700" data-testid="revenue-timeline-total">
              Total: {fmtCHF(total)}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0d9488" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#0d9488" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDay}
                tick={{ fontSize: 11, fill: '#6b7280' }}
                interval="preserveStartEnd"
                minTickGap={30}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                width={40}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#0d9488"
                strokeWidth={2}
                fill="url(#revGradient)"
                dot={false}
                activeDot={{ r: 4, fill: '#0d9488' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default RevenueTimelineChart;
